import { ChatContextType, resolveSubjectScopedKind, type SubjectScopedKind } from '@trinity/chat';
import { BiChatContextType } from '@trinity/chat/enums';
import type { BrandInsightsInstanceContext } from '@trinity/chat/types/chat.context';

const KIND_LABEL: Record<SubjectScopedKind, string> = {
  project: 'Project',
  folder: 'Folder',
  brand: 'Brand',
  document: 'Documents',
  transcript: 'Transcripts',
};

/** Display label per ChatContextType for the filter; null if not applicable. */
const FILTER_LABEL_MAP: Record<ChatContextType, string | null> = {
  [ChatContextType.Global]: 'Project',
  [ChatContextType.KolProfile]: null,
  [ChatContextType.Brand]: 'Brand',
  [ChatContextType.Document]: 'Documents',
  [ChatContextType.Folder]: 'Folder',
  [ChatContextType.Agent]: null, // Handled by Agent identifier
  [ChatContextType.Transcript]: 'Transcripts',
  [ChatContextType.Project]: 'Project', // shown read-only; isFilterEnabledForContext disables it
  [ChatContextType.MessageGeneration]: null,
  [ChatContextType.Trini]: null,
};

/** Returns the filter's display label for this context (Agent contexts resolve via their identifier), or null if it shouldn't show. */
export function getFilterLabelForContext(
  context: BrandInsightsInstanceContext.ItemWithMetadata
): string | null {
  // For Agent contexts, check the identifier to determine the label
  if (context.type === ChatContextType.Agent) {
    const kind = resolveSubjectScopedKind(context);
    if (kind) {
      return KIND_LABEL[kind];
    }
    if (context.data.identifier === BiChatContextType.Global) {
      return 'Everything';
    }

    // Unknown agent identifier - don't show filter
    return null;
  }

  return FILTER_LABEL_MAP[context.type] ?? null;
}

/** Placeholder text per label, mirroring Chat.Header/ContextSearch.tsx's `DisplayMap`. */
const FILTER_PLACEHOLDER_BY_LABEL: Record<string, string> = {
  Project: 'Search projects...',
  Documents: 'Search Documents',
  Folder: 'Search Folders',
  Brand: 'Search Brands',
  Transcripts: 'Search Transcripts',
  Everything: 'Search Everything',
};

/** Placeholder for the filter's search input; falls back to `Search {label}...`. */
export function getFilterPlaceholderForContext(
  context: BrandInsightsInstanceContext.ItemWithMetadata
): string {
  const label = getFilterLabelForContext(context);
  if (!label) return '';

  return FILTER_PLACEHOLDER_BY_LABEL[label] ?? `Search ${label}...`;
}

/** Whether the filter should be enabled (searchable) for this context. */
export function isFilterEnabledForContext(context: BrandInsightsInstanceContext.ItemWithMetadata): boolean {
  const label = getFilterLabelForContext(context);
  if (!label) return false;

  // Project context: read-only, shows current project
  if (context.type === ChatContextType.Project) {
    return false;
  }

  return true;
}
